import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Mail, MailOpen, Trash2, Plus, Edit, Eye, Search, ArrowUp, ArrowDown, FileText, Save, Upload, CheckSquare, Square, X } from 'lucide-react';
import { useAdmin } from '../hooks/useVentures';
import Analytics from './Analytics';
import axios from 'axios';

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    setError('');
    try {
      await onLogin(username, password);
    } catch {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cv-bg)] flex items-center justify-center" data-testid="admin-login-page">
      <motion.form
        onSubmit={handleSubmit}
        className="w-full max-w-sm p-8 border border-[var(--cv-border)]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-xl font-bold mb-6 uppercase tracking-widest" style={{ fontFamily: 'var(--font-heading)' }}>
          Admin
        </h1>
        <div className="flex flex-col gap-4 mb-4">
          <input
            type="text" value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="bg-transparent border-b border-[var(--cv-border)] py-2 text-white outline-none focus:border-white transition-colors text-sm"
            data-testid="admin-username-input"
          />
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-transparent border-b border-[var(--cv-border)] py-2 text-white outline-none focus:border-white transition-colors text-sm"
            data-testid="admin-password-input"
          />
        </div>
        {error && <p className="text-red-400 text-xs mb-3" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit" disabled={loading}
          className="w-full bg-white text-black py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
          data-testid="admin-login-btn"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </motion.form>
    </div>
  );
}

function ContactsPanel({ contacts, onMarkRead, onDelete, loading, onSearch, token, onRefresh }) {
  const [query, setQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const unread = contacts.filter((c) => !c.read).length;
  const headers = { Authorization: `Bearer ${token}` };

  const handleSearch = (q, u) => {
    setQuery(q);
    onSearch({ q: q || undefined, unread: u || undefined });
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === contacts.length) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = [...selected];
      if (action === 'delete') {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/bulk-delete`, { ids }, { headers });
      } else if (action === 'read') {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/bulk-read`, { ids }, { headers });
      }
      setSelected(new Set());
      onRefresh();
    } catch { /* silent */ } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div data-testid="admin-contacts-panel">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
          Messages
        </h2>
        {unread > 0 && (
          <span className="bg-white text-black text-xs px-2 py-0.5 font-bold">{unread} new</span>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="contacts-search-bar">
        <div className="flex items-center gap-2 flex-1 border-b border-[var(--cv-border)] pb-1">
          <Search size={14} className="text-[var(--cv-text-dim)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value, unreadOnly)}
            placeholder="Search by name or email..."
            className="bg-transparent text-white text-sm outline-none flex-1"
            data-testid="contacts-search-input"
          />
        </div>
        <button
          onClick={() => { setUnreadOnly(!unreadOnly); handleSearch(query, !unreadOnly); }}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${unreadOnly ? 'bg-white text-black border-white' : 'border-[var(--cv-border)] text-[var(--cv-text-dim)] hover:text-white hover:border-white'}`}
          data-testid="contacts-unread-toggle"
        >
          {unreadOnly ? 'Showing Unread' : 'Show Unread Only'}
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {contacts.length > 0 && (
        <div className="flex items-center gap-3 mb-4 py-2 border-b border-[var(--cv-border)]" data-testid="contacts-bulk-bar">
          <button onClick={toggleAll} className="p-1 hover:bg-white/10 transition-colors" data-testid="contacts-select-all">
            {selected.size === contacts.length ? <CheckSquare size={16} /> : <Square size={16} className="text-[var(--cv-text-dim)]" />}
          </button>
          <span className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider">
            {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
          </span>
          {selected.size > 0 && (
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => bulkAction('read')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-wider border border-[var(--cv-border)] hover:border-white transition-colors disabled:opacity-50"
                data-testid="contacts-bulk-read"
              >
                <MailOpen size={12} /> Mark Read
              </button>
              <button
                onClick={() => bulkAction('delete')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-wider border border-red-500/40 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                data-testid="contacts-bulk-delete"
              >
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={() => setSelected(new Set())} className="p-1 hover:bg-white/10 transition-colors" data-testid="contacts-clear-selection">
                <X size={14} className="text-[var(--cv-text-dim)]" />
              </button>
            </div>
          )}
        </div>
      )}

      {contacts.length === 0 && <p className="text-[var(--cv-text-dim)] text-sm">No messages found.</p>}
      <div className="flex flex-col gap-3">
        {contacts.map((c) => (
          <div key={c.id} className={`p-4 border ${c.read ? 'border-[var(--cv-border)] opacity-60' : 'border-white/20 bg-white/[0.02]'} ${selected.has(c.id) ? 'border-white/40 bg-white/[0.04]' : ''}`} data-testid={`contact-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <button onClick={() => toggleSelect(c.id)} className="p-0.5 mt-0.5 shrink-0" data-testid={`select-contact-${c.id}`}>
                  {selected.has(c.id) ? <CheckSquare size={14} /> : <Square size={14} className="text-[var(--cv-text-dim)]" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">{c.name}</span>
                    <span className="text-xs text-[var(--cv-text-dim)]">{c.email}</span>
                  </div>
                  <p className="text-sm text-[var(--cv-text-dim)] line-clamp-2">{c.message}</p>
                  <p className="text-[0.6rem] text-[var(--cv-text-dim)] mt-1 uppercase tracking-wider" style={{ fontFamily: 'var(--font-mono)' }}>
                    {new Date(c.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {!c.read && (
                  <button onClick={() => onMarkRead(c.id)} className="p-1.5 hover:bg-white/10 transition-colors" title="Mark read" data-testid={`mark-read-${c.id}`}>
                    <MailOpen size={14} />
                  </button>
                )}
                <button onClick={() => onDelete(c.id)} className="p-1.5 hover:bg-red-500/20 text-red-400 transition-colors" title="Delete" data-testid={`delete-contact-${c.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VentureForm({ venture, onSubmit, onCancel, token }) {
  const [form, setForm] = useState(venture || {
    title: '', tag: '', year: new Date().getFullYear().toString(), description: '',
    challenge: '', approach: '', outcome: '', img: '', video: '', size: 'medium', featured: false,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress('Uploading...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, img: res.data.url }));
      setUploadProgress('Uploaded!');
      setTimeout(() => setUploadProgress(''), 2000);
    } catch (e) {
      setUploadProgress(e.response?.data?.detail || 'Upload failed');
      setTimeout(() => setUploadProgress(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.tag || !form.year) return;
    onSubmit(form);
  };

  const fields = [
    { key: 'title', label: 'Title', required: true },
    { key: 'tag', label: 'Tag (e.g. Mobility Infrastructure)', required: true },
    { key: 'year', label: 'Year', required: true },
    { key: 'video', label: 'Video URL' },
    { key: 'size', label: 'Size (medium / large span-2)' },
  ];
  const textareas = [
    { key: 'description', label: 'Description' },
    { key: 'challenge', label: 'Challenge' },
    { key: 'approach', label: 'Approach' },
    { key: 'outcome', label: 'Outcome' },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="venture-form">
      {fields.map((f) => (
        <div key={f.key} className="flex flex-col gap-1">
          <label className="text-[0.65rem] uppercase tracking-wider text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }}>
            {f.label}{f.required && ' *'}
          </label>
          <input
            value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            className="bg-transparent border-b border-[var(--cv-border)] py-2 text-white text-sm outline-none focus:border-white transition-colors"
            data-testid={`venture-form-${f.key}`}
          />
        </div>
      ))}

      {/* Image Upload */}
      <div className="flex flex-col gap-2">
        <label className="text-[0.65rem] uppercase tracking-wider text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }}>
          Image (upload or paste URL)
        </label>
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border border-dashed border-[var(--cv-border)] hover:border-white/40 transition-colors p-4 flex flex-col items-center gap-2 cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="venture-form-img-dropzone"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="venture-form-img-file"
          />
          <Upload size={20} className="text-[var(--cv-text-dim)]" />
          <span className="text-xs text-[var(--cv-text-dim)]">
            {uploading ? 'Uploading...' : 'Drop image here or click to browse'}
          </span>
          {uploadProgress && <span className="text-xs text-white">{uploadProgress}</span>}
        </div>
        <input
          value={form.img || ''}
          onChange={(e) => setForm({ ...form, img: e.target.value })}
          placeholder="Or paste image URL..."
          className="bg-transparent border-b border-[var(--cv-border)] py-2 text-white text-sm outline-none focus:border-white transition-colors"
          data-testid="venture-form-img"
        />
        {form.img && (
          <div className="mt-1">
            <img src={form.img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${form.img}` : form.img} alt="Preview" className="max-h-32 object-cover border border-[var(--cv-border)]" data-testid="venture-form-img-preview" />
          </div>
        )}
      </div>

      {textareas.map((f) => (
        <div key={f.key} className="flex flex-col gap-1">
          <label className="text-[0.65rem] uppercase tracking-wider text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }}>
            {f.label}
          </label>
          <textarea
            value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3}
            className="bg-transparent border-b border-[var(--cv-border)] py-2 text-white text-sm outline-none focus:border-white transition-colors resize-none"
            data-testid={`venture-form-${f.key}`}
          />
        </div>
      ))}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="venture-form-featured" />
        Featured on homepage
      </label>
      <div className="flex gap-3 mt-2">
        <button type="submit" className="bg-white text-black px-6 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-gray-200 transition-colors" data-testid="venture-form-submit">
          {venture ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2 text-xs uppercase tracking-widest border border-[var(--cv-border)] hover:border-white transition-colors" data-testid="venture-form-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}

function VenturesPanel({ ventures, onCreate, onUpdate, onDelete, onReorder, token }) {
  const [editing, setEditing] = useState(null); // null | 'new' | venture object

  const handleMoveUp = (venture, index) => {
    if (index === 0) return;
    const prev = ventures[index - 1];
    onReorder(venture.id, prev.sort_order ?? index - 1);
    onReorder(prev.id, venture.sort_order ?? index);
  };

  const handleMoveDown = (venture, index) => {
    if (index === ventures.length - 1) return;
    const next = ventures[index + 1];
    onReorder(venture.id, next.sort_order ?? index + 1);
    onReorder(next.id, venture.sort_order ?? index);
  };

  return (
    <div data-testid="admin-ventures-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
          Ventures ({ventures.length})
        </h2>
        {!editing && (
          <button onClick={() => setEditing('new')} className="flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-wider border border-[var(--cv-border)] hover:border-white transition-colors" data-testid="add-venture-btn">
            <Plus size={12} /> Add
          </button>
        )}
      </div>

      {editing && (
        <div className="mb-8 p-6 border border-white/20">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            {editing === 'new' ? 'New Venture' : `Edit: ${editing.title}`}
          </h3>
          <VentureForm
            venture={editing === 'new' ? null : editing}
            token={token}
            onSubmit={async (data) => {
              if (editing === 'new') await onCreate(data);
              else await onUpdate(editing.id, data);
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        {ventures.map((v, idx) => (
          <div key={v.id} className="flex items-center justify-between p-4 border border-[var(--cv-border)]" data-testid={`admin-venture-${v.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveUp(v, idx)}
                  disabled={idx === 0}
                  className="p-0.5 hover:bg-white/10 transition-colors disabled:opacity-20"
                  title="Move up"
                  data-testid={`venture-move-up-${v.id}`}
                >
                  <ArrowUp size={12} />
                </button>
                <button
                  onClick={() => handleMoveDown(v, idx)}
                  disabled={idx === ventures.length - 1}
                  className="p-0.5 hover:bg-white/10 transition-colors disabled:opacity-20"
                  title="Move down"
                  data-testid={`venture-move-down-${v.id}`}
                >
                  <ArrowDown size={12} />
                </button>
              </div>
              <div>
                <span className="text-sm font-semibold">{v.title}</span>
                <span className="text-xs text-[var(--cv-text-dim)] ml-2">{v.tag} &mdash; {v.year}</span>
                {v.featured && <span className="text-[0.6rem] bg-white/10 px-1.5 py-0.5 ml-2 uppercase tracking-wider">Featured</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <a href={`/ventures/${v.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white/10 transition-colors" title="View">
                <Eye size={14} />
              </a>
              <button onClick={() => setEditing(v)} className="p-1.5 hover:bg-white/10 transition-colors" title="Edit" data-testid={`edit-venture-${v.id}`}>
                <Edit size={14} />
              </button>
              <button onClick={() => onDelete(v.id)} className="p-1.5 hover:bg-red-500/20 text-red-400 transition-colors" title="Delete" data-testid={`delete-venture-${v.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const admin = useAdmin();
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    if (admin.token) {
      admin.fetchContacts();
      admin.fetchVentures();
    }
  }, [admin.token]);

  if (!admin.token) return <LoginForm onLogin={admin.login} />;

  return (
    <div className="min-h-screen bg-[var(--cv-bg)]" data-testid="admin-dashboard">
      {/* Admin header */}
      <div className="border-b border-[var(--cv-border)] px-6 md:px-12 py-4 flex items-center justify-between max-w-[1200px] mx-auto">
        <div className="flex items-center gap-6">
          <span className="text-sm font-bold uppercase tracking-widest" style={{ fontFamily: 'var(--font-heading)' }}>
            CV Admin
          </span>
          <div className="flex gap-1">
            {['dashboard', 'contacts', 'ventures', 'posts'].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1 text-xs uppercase tracking-wider transition-colors ${tab === t ? 'bg-white text-black' : 'text-[var(--cv-text-dim)] hover:text-white'}`}
                data-testid={`admin-tab-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <button onClick={admin.logout} className="flex items-center gap-2 text-xs text-[var(--cv-text-dim)] hover:text-white transition-colors" data-testid="admin-logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-8">
        {tab === 'dashboard' && (
          <Analytics token={admin.token} />
        )}
        {tab === 'contacts' && (
          <ContactsPanel
            contacts={admin.contacts}
            loading={admin.loading}
            onMarkRead={admin.markContactRead}
            onDelete={admin.deleteContact}
            onSearch={(params) => admin.fetchContacts(params)}
            token={admin.token}
            onRefresh={() => admin.fetchContacts()}
          />
        )}
        {tab === 'ventures' && (
          <VenturesPanel
            ventures={admin.ventures}
            onCreate={admin.createVenture}
            onUpdate={admin.updateVenture}
            onDelete={admin.deleteVenture}
            onReorder={admin.reorderVenture}
            token={admin.token}
          />
        )}
        {tab === 'posts' && (
          <PostsPanel token={admin.token} />
        )}
      </div>
    </div>
  );
}


/* ── Posts Management Panel ── */
const POSTS_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PostsPanel({ token }) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${POSTS_API}/posts?all=true`, { headers });
      setPosts(res.data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCreate = async (data) => {
    await axios.post(`${POSTS_API}/posts`, data, { headers });
    setEditing(null);
    fetchPosts();
  };

  const handleUpdate = async (id, data) => {
    await axios.put(`${POSTS_API}/posts/${id}`, data, { headers });
    setEditing(null);
    fetchPosts();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${POSTS_API}/posts/${id}`, { headers });
    fetchPosts();
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === posts.length) setSelected(new Set());
    else setSelected(new Set(posts.map((p) => p.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await axios.post(`${POSTS_API}/posts/bulk-delete`, { ids: [...selected] }, { headers });
      setSelected(new Set());
      fetchPosts();
    } catch { /* silent */ } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div data-testid="admin-posts-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
          Journal Posts ({posts.length})
        </h2>
        {!editing && (
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-wider border border-[var(--cv-border)] hover:border-white transition-colors"
            data-testid="add-post-btn"
          >
            <Plus size={12} /> New Post
          </button>
        )}
      </div>

      {editing && (
        <PostForm
          post={editing === 'new' ? null : editing}
          onSubmit={async (data) => {
            if (editing === 'new') await handleCreate(data);
            else await handleUpdate(editing.id, data);
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Bulk Actions Bar */}
      {posts.length > 0 && (
        <div className="flex items-center gap-3 mb-4 py-2 border-b border-[var(--cv-border)]" data-testid="posts-bulk-bar">
          <button onClick={toggleAll} className="p-1 hover:bg-white/10 transition-colors" data-testid="posts-select-all">
            {selected.size === posts.length ? <CheckSquare size={16} /> : <Square size={16} className="text-[var(--cv-text-dim)]" />}
          </button>
          <span className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider">
            {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
          </span>
          {selected.size > 0 && (
            <div className="flex gap-2 ml-auto">
              <button
                onClick={bulkDelete}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-wider border border-red-500/40 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                data-testid="posts-bulk-delete"
              >
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={() => setSelected(new Set())} className="p-1 hover:bg-white/10 transition-colors" data-testid="posts-clear-selection">
                <X size={14} className="text-[var(--cv-text-dim)]" />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {posts.map((p) => (
          <div key={p.id} className={`flex items-center justify-between p-4 border border-[var(--cv-border)] ${selected.has(p.id) ? 'border-white/40 bg-white/[0.04]' : ''}`} data-testid={`admin-post-${p.id}`}>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleSelect(p.id)} className="p-0.5 shrink-0" data-testid={`select-post-${p.id}`}>
                {selected.has(p.id) ? <CheckSquare size={14} /> : <Square size={14} className="text-[var(--cv-text-dim)]" />}
              </button>
              <FileText size={14} className="text-[var(--cv-text-dim)]" />
              <div>
                <span className="text-sm font-semibold">{p.title}</span>
                <span className="text-xs text-[var(--cv-text-dim)] ml-2">/{p.slug}</span>
                <span className={`text-[0.6rem] px-1.5 py-0.5 ml-2 uppercase tracking-wider ${p.published ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-[var(--cv-text-dim)]'}`}>
                  {p.published ? 'Published' : 'Draft'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {p.published && (
                <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white/10 transition-colors" title="View">
                  <Eye size={14} />
                </a>
              )}
              <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-white/10 transition-colors" title="Edit" data-testid={`edit-post-${p.id}`}>
                <Edit size={14} />
              </button>
              <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-500/20 text-red-400 transition-colors" title="Delete" data-testid={`delete-post-${p.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostForm({ post, onSubmit, onCancel }) {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [body, setBody] = useState(post?.body || '');
  const [published, setPublished] = useState(post?.published || false);
  const [error, setError] = useState('');

  const autoSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <div className="mb-8 p-6 border border-white/20">
      <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        {post ? `Edit: ${post.title}` : 'New Post'}
      </h3>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider block mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); if (!post) setSlug(autoSlug(e.target.value)); }}
              className="w-full bg-transparent border border-[var(--cv-border)] px-3 py-2 text-white text-sm outline-none focus:border-white"
              data-testid="post-title-input"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider block mb-1">Slug</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full bg-transparent border border-[var(--cv-border)] px-3 py-2 text-white text-sm outline-none focus:border-white"
              data-testid="post-slug-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider block mb-1">Body (use double newlines for paragraphs)</label>
          <textarea
            rows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full bg-transparent border border-[var(--cv-border)] px-3 py-2 text-white text-sm outline-none focus:border-white resize-y"
            style={{ fontFamily: 'var(--font-body)' }}
            data-testid="post-body-input"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-white" data-testid="post-published-toggle" />
          <span className="text-xs text-[var(--cv-text-dim)] uppercase tracking-wider">Publish</span>
        </label>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-3">
          <button
            onClick={async () => {
              setError('');
              if (!title || !slug || !body) { setError('All fields required.'); return; }
              try { await onSubmit({ title, slug, body, published }); } catch (e) { setError(e.response?.data?.detail || 'Error saving post.'); }
            }}
            className="flex items-center gap-2 px-6 py-2 text-xs uppercase tracking-widest bg-white text-black font-bold hover:bg-[var(--cv-text-dim)] transition-colors"
            data-testid="post-form-save"
          >
            <Save size={12} /> Save
          </button>
          <button onClick={onCancel} className="px-6 py-2 text-xs uppercase tracking-widest border border-[var(--cv-border)] hover:border-white transition-colors" data-testid="post-form-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
